import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { useBuild } from '../contexts/BuildContext';

const CoinLabelsSection = ({ onUpdate, boostInput = false, fieldName = 'coin_labels' }) => {
  const { fetchCachedList, selectedIndex } = useBuild();
  const [currentCoinLabel, setCurrentCoinLabel] = useState('');
  const [boost, setBoost] = useState(1);
  const [coinLabelOptions, setCoinLabelOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch coin label options on component mount and when selectedIndex changes
  useEffect(() => {
    const fetchCoinLabelOptions = async () => {
      try {
        setLoading(true);
        setError(null);
        const endpoint = `/search/frequent_values/${selectedIndex}/coin_labels`;
        const coinLabelsData = await fetchCachedList(endpoint);
        setCoinLabelOptions(coinLabelsData);
      } catch (err) {
        console.error('Failed to fetch coin label options:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCoinLabelOptions();
  }, [fetchCachedList, selectedIndex]);

  const handleCoinLabelAction = (actionType) => {
    if (!currentCoinLabel) return;
    
    const updateData = {
      action: actionType,
      filter: 'terms',
      field: fieldName,
      value: [currentCoinLabel]
    };
    
    // Add boost value if boostInput is enabled
    if (boostInput) {
      updateData.boost = boost;
    }
    
    onUpdate(updateData);
    setCurrentCoinLabel(''); // Reset after adding
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error">
          Failed to load coin labels: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Coin Label</InputLabel>
        <Select
          value={currentCoinLabel}
          label="Coin Label"
          onChange={(e) => setCurrentCoinLabel(e.target.value)}
          disabled={loading}
        >
          {coinLabelOptions.map((label) => (
            <MenuItem key={label.id} value={label.id}>
              {label.id} ({label.count})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      {boostInput && (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Boost"
          type="number"
          value={boost}
          onChange={(e) => setBoost(parseFloat(e.target.value) || 1)}
          disabled={loading}
        />
      )}
      
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleCoinLabelAction('include')}
          disabled={!currentCoinLabel || loading}
        >
          <AddIcon />
        </Button>
        
        {boostInput && (
          <Button
            variant="contained"
            color="success"
            onClick={() => handleCoinLabelAction('boost')}
            disabled={!currentCoinLabel || loading}
          >
            <TrendingUpIcon />
          </Button>
        )}
        
        <Button
          variant="contained"
          color="error"
          onClick={() => handleCoinLabelAction('exclude')}
          disabled={!currentCoinLabel || loading}
        >
          <RemoveIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default CoinLabelsSection;

